import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Share2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import BottomNav from "../components/BottomNav";

// Referral Level System
const REFERRAL_LEVELS = [
  { name: "Starter", icon: "🌱", minJoins: 0, maxJoins: 49, color: "from-gray-500 to-gray-600", reward: null },
  { name: "Bronze Referrer", icon: "🥉", minJoins: 50, maxJoins: 99, color: "from-amber-700 to-amber-800", reward: null },
  { name: "Silver Referrer", icon: "🥈", minJoins: 100, maxJoins: 499, color: "from-gray-300 to-gray-400", reward: null },
  { name: "Gold Referrer", icon: "🥇", minJoins: 500, maxJoins: 999, color: "from-yellow-400 to-yellow-600", reward: "$1,000 Bonus" },
  { name: "Leader", icon: "👑", minJoins: 1000, maxJoins: Infinity, color: "from-purple-500 to-pink-500", reward: "$10,000 Bonus" },
];

const getCurrentLevel = (referrals) => {
  for (let i = REFERRAL_LEVELS.length - 1; i >= 0; i--) {
    if (referrals >= REFERRAL_LEVELS[i].minJoins) return i;
  }
  return 0;
};

const getNextLevel = (currentLevelIndex) => {
  if (currentLevelIndex < REFERRAL_LEVELS.length - 1) {
    return REFERRAL_LEVELS[currentLevelIndex + 1];
  }
  return null;
};

const ReferPage = () => {
  const { user, authAxios } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [incomeDetails, setIncomeDetails] = useState(null);
  const [myReferrals, setMyReferrals] = useState(null);
  const [showReferralsList, setShowReferralsList] = useState(false);

  useEffect(() => { 
    fetchStats(); 
    fetchIncomeDetails();
    fetchMyReferrals();
  }, []);

  const fetchStats = async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/refer/stats/${user.id}`);
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchIncomeDetails = async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/refer/income-details/${user.id}`);
      setIncomeDetails(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchMyReferrals = async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/refer/my-referrals/${user.id}`);
      setMyReferrals(res.data);
    } catch (e) { console.error(e); }
  };

  const code = stats?.referral_code || user.referral_code;
  const referralLink = `${window.location.origin}/register?ref=${code}`;
  const referralsCount = stats?.referrals_count || 0;
  const currentLevelIndex = getCurrentLevel(referralsCount);
  const currentLevel = REFERRAL_LEVELS[currentLevelIndex];
  const nextLevel = getNextLevel(currentLevelIndex);
  
  const progressToNext = nextLevel 
    ? ((referralsCount - currentLevel.minJoins) / (nextLevel.minJoins - currentLevel.minJoins)) * 100
    : 100;
  const joinsToNext = nextLevel ? nextLevel.minJoins - referralsCount : 0;
  
  const copy = () => { 
    navigator.clipboard.writeText(referralLink); 
    toast.success("Referral link copied!"); 
  };
  
  const share = () => {
    const text = `🎉 Join Lite Gold and get 1000 LTG bonus!\n\n💰 Earn daily rewards with LTG staking\n🎰 Free spins every 12 hours\n\n👉 Register now: ${referralLink}`;
    if (navigator.share) {
      navigator.share({ 
        title: "Join Lite Gold - Get 1000 LTG Free!", 
        text: text,
        url: referralLink
      });
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Referral message copied!");
    }
  };

  return (
    <div className="min-h-screen pb-24 p-4" data-testid="refer-page">
      <h1 className="font-manrope font-bold text-2xl mb-4">{t("referEarn")}</h1>

      <div className={`relative overflow-hidden bg-gradient-to-r ${currentLevel.color} rounded-2xl p-5 mb-4 shadow-lg`}>
        <div className="absolute top-0 right-0 opacity-20 text-8xl transform translate-x-4 -translate-y-2">
          {currentLevel.icon}
        </div>
        <div className="relative z-10">
          <p className="text-white/80 text-sm mb-1">Your Level</p>
          <div className="flex items-center gap-3">
            <span className="text-4xl">{currentLevel.icon}</span>
            <div>
              <h2 className="font-manrope font-bold text-2xl text-white">{currentLevel.name}</h2>
              <p className="text-white/90 text-sm">{referralsCount} Referrals Complete</p>
            </div>
          </div>
          {currentLevel.reward && (
            <div className="mt-3 bg-white/20 rounded-lg px-3 py-2 inline-block">
              <p className="text-white font-bold">🎁 Reward: {currentLevel.reward} Bonus</p>
            </div>
          )}
        </div>
      </div>

      {nextLevel && (
        <div className="bg-card border border-border rounded-xl p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-muted-foreground text-sm">Progress to {nextLevel.icon} {nextLevel.name}</span>
            <span className="text-primary font-bold">{joinsToNext} more</span>
          </div>
          <div className="h-3 bg-secondary rounded-full overflow-hidden">
            <div 
              className={`h-full bg-gradient-to-r ${nextLevel.color} rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(progressToNext, 100)}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-muted-foreground">
            <span>{currentLevel.minJoins}</span>
            <span>{nextLevel.minJoins} joins</span>
          </div>
          {nextLevel.reward && (
            <p className="text-center text-green-400 text-sm mt-2">🎁 Unlock {nextLevel.reward} Bonus at {nextLevel.minJoins} joins!</p>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-4 mb-4">
        <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
          <span className="text-primary">🏆</span> Referral Levels
        </h3>
        <div className="space-y-2">
          {REFERRAL_LEVELS.map((level, idx) => (
            <div 
              key={level.name}
              className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                idx === currentLevelIndex 
                  ? `bg-gradient-to-r ${level.color} text-white` 
                  : idx < currentLevelIndex 
                    ? 'bg-green-500/20 border border-green-500/30' 
                    : 'bg-secondary/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{level.icon}</span>
                <div>
                  <p className={`font-bold ${idx === currentLevelIndex ? 'text-white' : ''}`}>{level.name}</p>
                  <p className={`text-xs ${idx === currentLevelIndex ? 'text-white/80' : 'text-muted-foreground'}`}>
                    {level.minJoins}+ Joins
                  </p>
                </div>
              </div>
              <div className="text-right">
                {level.reward && (
                  <p className={`text-sm font-bold ${idx === currentLevelIndex ? 'text-white' : 'text-green-400'}`}>
                    {level.reward}
                  </p>
                )}
                {idx < currentLevelIndex && <span className="text-green-400">✓</span>}
                {idx === currentLevelIndex && <span className="bg-white/20 px-2 py-1 rounded text-xs">Current</span>}
              </div>
            </div>
          ))}
        </div>
        
        {/* Important Note about Active Joins */}
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-xs text-center">
            ⚠️ <strong>Important:</strong> $1,000 and $10,000 bonuses are only for <strong>Active Joins</strong>. Fake referrals will not count.
          </p>
        </div>
      </div>

      <div className="bg-card border border-primary/30 rounded-2xl p-5 text-center mb-4">
        <p className="text-muted-foreground mb-2">{t("yourCode")}</p>
        <p className="font-manrope text-3xl font-bold text-primary tracking-widest">{code}</p>
        <div className="flex justify-center gap-3 mt-4">
          <button onClick={copy} className="px-5 py-2 bg-secondary rounded-full flex items-center gap-2 text-sm" data-testid="copy-referral-btn">
            <Copy size={14} /> Copy
          </button>
          <button onClick={share} className="px-5 py-2 bg-green-600 rounded-full flex items-center gap-2 text-sm" data-testid="share-referral-btn">
            <Share2 size={14} /> {t("share")}
          </button>
        </div>
      </div>

      <div className="flex justify-around text-center bg-card border border-border rounded-xl p-4 mb-4">
        <div>
          <p className="text-muted-foreground text-sm">{t("referrals")}</p>
          <p className="font-manrope text-2xl font-bold">{referralsCount}</p>
        </div>
        <div className="w-px bg-border" />
        <div>
          <p className="text-muted-foreground text-sm">Referral {t("earned")}</p>
          <p className="font-manrope text-2xl font-bold text-primary">{stats?.total_earned || 0} LTG</p>
        </div>
      </div>

      {/* My Referrals Deposit Status */}
      {myReferrals && myReferrals.total_referrals > 0 && (
        <div className="mb-4 bg-gradient-to-r from-orange-900/40 via-yellow-900/30 to-orange-900/40 border border-orange-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">👥</span>
              <h3 className="font-bold text-lg text-orange-400">My Referrals Status</h3>
            </div>
            <button 
              onClick={() => setShowReferralsList(!showReferralsList)}
              className="text-xs bg-orange-500/20 px-3 py-1 rounded-full text-orange-400"
            >
              {showReferralsList ? 'Hide' : 'View All'}
            </button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-black/30 p-3 rounded-lg text-center">
              <p className="text-muted-foreground text-xs">Total</p>
              <p className="text-white font-bold text-xl">{myReferrals.total_referrals}</p>
            </div>
            <div className="bg-green-500/20 p-3 rounded-lg text-center">
              <p className="text-green-400 text-xs">Deposited ✓</p>
              <p className="text-green-400 font-bold text-xl">{myReferrals.deposited_count}</p>
            </div>
            <div className="bg-red-500/20 p-3 rounded-lg text-center">
              <p className="text-red-400 text-xs">Not Deposited</p>
              <p className="text-red-400 font-bold text-xl">{myReferrals.not_deposited_count}</p>
            </div>
          </div>

          {/* 10% Bonus Message */}
          {myReferrals.not_deposited_count > 0 && (
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3 mb-3">
              <p className="text-yellow-400 text-sm text-center">
                💰 <strong>{myReferrals.not_deposited_count} people</strong> haven't deposited yet!<br/>
                <span className="text-xs">When they deposit, you earn <strong>10% bonus</strong> on all their deposits!</span>
              </p>
            </div>
          )}
          
          {/* Pending 1000 LTG Bonus Message - For ALL users */}
          {myReferrals.not_deposited_count > 0 && (
            <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-3 mb-3">
              <p className="text-purple-400 text-sm text-center">
                🎁 <strong>1000 LTG Bonus Pending!</strong><br/>
                <span className="text-xs">Aapko 1000 LTG bonus tab milega jab aapke referred users apna account activate karenge ($50 deposit required).</span>
              </p>
            </div>
          )}

          {/* Referrals List */}
          {showReferralsList && (
            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
              {/* Not Deposited Users First */}
              {myReferrals.not_deposited_users.length > 0 && (
                <>
                  <p className="text-red-400 text-xs font-bold">❌ Not Deposited ({myReferrals.not_deposited_users.length})</p>
                  {/* Pending Bonus - For ALL users */}
                  {(
                    <p className="text-purple-400 text-xs mb-2 italic">
                      👆 In users ke activate hone par aapko 1000 LTG bonus milega
                    </p>
                  )}
                  {myReferrals.not_deposited_users.map((ref, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-red-500/10 border border-red-500/20 rounded-lg p-2">
                      <div>
                        <p className="text-white text-sm font-medium">{ref.full_name}</p>
                        <p className="text-muted-foreground text-xs">Joined: {ref.joined_date}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-red-400 text-xs bg-red-500/20 px-2 py-1 rounded">Not Deposited</span>
                        {/* Bonus Pending - For ALL users */}
                      {(
                        <p className="text-purple-400 text-[10px] mt-1">Bonus Pending</p>
                      )}
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {/* Deposited Users */}
              {myReferrals.deposited_users.length > 0 && (
                <>
                  <p className="text-green-400 text-xs font-bold mt-3">✓ Deposited ({myReferrals.deposited_users.length})</p>
                  {myReferrals.deposited_users.map((ref, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-green-500/10 border border-green-500/20 rounded-lg p-2">
                      <div>
                        <p className="text-white text-sm font-medium">{ref.full_name}</p>
                        <p className="text-muted-foreground text-xs">Joined: {ref.joined_date}</p>
                      </div>
                      <span className="text-green-400 text-xs bg-green-500/20 px-2 py-1 rounded">✓ Done</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Referral Income in Stake - Premium Card */}
      {(stats?.total_earned > 0 || (incomeDetails && incomeDetails.total_referral_earnings > 0)) && (
        <div className="mb-4 p-4 bg-gradient-to-r from-purple-900/40 via-pink-900/30 to-purple-900/40 border border-purple-500/30 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">💎</span>
            <h3 className="font-bold text-lg text-purple-400">{t("referralEarnedStaked")}</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-black/30 p-3 rounded-lg text-center">
              <p className="text-muted-foreground text-xs mb-1">Referral Earned</p>
              <p className="text-primary font-bold text-xl">{(stats?.total_earned || 0).toLocaleString()}</p>
              <p className="text-primary/70 text-xs">LTG</p>
            </div>
            <div className="bg-black/30 p-3 rounded-lg text-center">
              <p className="text-muted-foreground text-xs mb-1">Referral in Stake</p>
              <p className="text-green-400 font-bold text-xl">{(stats?.total_earned || 0).toLocaleString()}</p>
              <p className="text-green-400/70 text-xs">LTG (1%/day)</p>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-purple-500/20">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">USDT Value:</span>
              <span className="text-yellow-400 font-bold">${((stats?.total_earned || 0) / 1000).toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner - All stake on main page */}
      <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-center gap-3">
        <span className="text-xl">ℹ️</span>
        <p className="text-blue-400 text-sm">{t("allStakeEarningHome")}</p>
      </div>

      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
        <p className="text-green-400 font-bold mb-1">🎁 {t("referEarn")} 1000 LTG!</p>
        <p className="text-muted-foreground text-sm">{t("referEarnDesc")}</p>
      </div>

      <BottomNav />
    </div>
  );
};

export default ReferPage;
